import { motion } from "framer-motion";
import {
  Calendar,
  CheckCircle,
  RefreshCw,
  Unlink,
  Video,
  AlertCircle,
  ExternalLink,
  CalendarCheck,
  CalendarX,
  Bell,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

// ----------------------------------------------------------------------
// Calendar Integration Mockup
// ----------------------------------------------------------------------

function CalendarIntegrationMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true }}
      className="bg-neutral-900 rounded-[20px] overflow-hidden border border-neutral-800"
    >
      {/* Window Chrome */}
      <div className="h-10 border-b border-neutral-800 flex items-center px-4 gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="h-6 px-4 rounded bg-neutral-800 flex items-center text-xs text-neutral-400">
            uselint.com/settings/calendar
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-5 w-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              Calendar Integration
            </h3>
          </div>
          <p className="text-sm text-neutral-400">
            Connect your Google Calendar to automatically sync booked sessions
            and check your availability
          </p>
        </div>

        {/* Connection Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          viewport={{ once: true }}
          className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4 mb-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Google Calendar Icon */}
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white border shadow-sm">
                <svg
                  viewBox="0 0 24 24"
                  className="h-8 w-8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4Z"
                    stroke="#4285F4"
                    strokeWidth="2"
                  />
                  <path d="M4 9H20" stroke="#4285F4" strokeWidth="2" />
                  <path d="M9 4V9" stroke="#4285F4" strokeWidth="2" />
                  <path d="M15 4V9" stroke="#4285F4" strokeWidth="2" />
                  <circle cx="8" cy="13" r="1" fill="#EA4335" />
                  <circle cx="12" cy="13" r="1" fill="#FBBC04" />
                  <circle cx="16" cy="13" r="1" fill="#34A853" />
                  <circle cx="8" cy="17" r="1" fill="#EA4335" />
                  <circle cx="12" cy="17" r="1" fill="#FBBC04" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-white">Google Calendar</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Connected
                  </span>
                </div>
                <p className="text-sm text-neutral-400">
                  testinvoiccer@gmail.com
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Last synced: 32m ago
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:justify-end">
              <button className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-neutral-600 text-neutral-300 hover:bg-neutral-700 transition-colors">
                <RefreshCw className="mr-1 h-4 w-4" />
                Sync
              </button>
              <button className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-neutral-600 text-red-400 hover:bg-neutral-700 transition-colors">
                <Unlink className="mr-1 h-4 w-4" />
                Disconnect
              </button>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-neutral-700 my-4" />

          {/* Auto-create Meet Links Setting */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-neutral-400" />
                <span className="text-sm font-medium text-white">
                  Auto-create Google Meet links
                </span>
              </div>
              {/* Toggle Switch (ON state) */}
              <div className="relative">
                <div className="w-11 h-6 bg-emerald-500 rounded-full">
                  <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform" />
                </div>
              </div>
            </div>
            <p className="text-xs text-neutral-500 pl-6">
              Automatically generate Google Meet links for new bookings when the
              meeting platform is set to Google Meet.
            </p>
          </div>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          viewport={{ once: true }}
          className="rounded-lg bg-neutral-800/30 p-4 mb-4"
        >
          <h4 className="text-sm font-medium text-white mb-3">
            What happens when you connect?
          </h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                New bookings are automatically added to your Google Calendar
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                Your existing calendar events are checked to prevent
                double-bookings
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                Google Meet links can be automatically generated for video calls
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                Calendar invites are sent to both you and your clients
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Privacy Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          viewport={{ once: true }}
          className="flex items-start gap-2 text-xs text-neutral-500"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            We only read your calendar to check availability (busy/free times).
            We don't access the content of your existing events. You can
            disconnect at any time.{" "}
            <span className="text-emerald-400 hover:underline inline-flex items-center gap-1 cursor-pointer">
              Google Privacy Policy
              <ExternalLink className="h-3 w-3" />
            </span>
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ----------------------------------------------------------------------
// Features
// ----------------------------------------------------------------------

const features = [
  {
    icon: CalendarCheck,
    title: "Automatic Event Sync",
    description: "Bookings instantly appear in your Google Calendar",
  },
  {
    icon: CalendarX,
    title: "Conflict Prevention",
    description: "Real-time availability checks prevent double-bookings",
  },
  {
    icon: Video,
    title: "Google Meet Integration",
    description: "Auto-generate video call links for your sessions",
  },
  {
    icon: Bell,
    title: "Smart Notifications",
    description: "Both you and clients receive calendar invites",
  },
];

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------

export function CalendarIntegrationShowcase() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-6">
              Sync Your Calendar.{" "}
              <span className="text-emerald-500">Never Double-Book.</span>
            </SectionHeading>

            <SectionSubheading className="mb-8">
              Connect your Google Calendar to automatically sync bookings, check
              availability in real-time, and generate video call links — all
              without lifting a finger.
            </SectionSubheading>

            <div className="space-y-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Calendar Integration Mockup */}
          <div className="relative rounded-[32px] border border-neutral-200/50 dark:border-neutral-700/50 bg-neutral-100/80 dark:bg-neutral-800/50 p-2 md:p-4">
            <div className="relative rounded-[24px] overflow-hidden">
              <CalendarIntegrationMockup />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

